import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export function Hero() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative isolate flex min-h-[60svh] items-center overflow-hidden pt-28 pb-20 sm:pt-32 sm:pb-24"
      style={{
        background:
          "linear-gradient(180deg, var(--color-section-bg) 0%, oklch(98% 0.012 84) 60%, oklch(97% 0.018 84) 100%)",
      }}
    >
      {/* hairline grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--color-warm-border) 1px, transparent 1px)",
          backgroundSize: "calc(100% / 12) 100%",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          {/* Heading */}
          <h1 className="font-heading text-4xl leading-[1.05] font-bold tracking-tight text-foreground sm:text-5xl lg:text-[5.5rem] xl:text-[6rem]">
            {t("forCompaniesPage.hero.title")}{" "}
            <span
              className="relative inline-block italic"
              style={{ color: "var(--color-brand-dark)" }}
            >
              {t("forCompaniesPage.hero.titleHighlight")}
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 h-[3px] w-full origin-left"
                style={{
                  background:
                    "linear-gradient(90deg, var(--color-brand) 0%, transparent 100%)",
                }}
              />
            </span>
            <span className="text-foreground">
              {t("forCompaniesPage.hero.titleAfter")}
            </span>
          </h1>

          {/* Sub */}
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:mt-8">
            {t("forCompaniesPage.hero.subtitle")}
          </p>

          {/* CTAs */}
          <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-5 md:mt-11">
            <Link
              to="/kontakt-firme"
              className="group inline-flex h-13 items-center justify-center gap-2.5 rounded-full bg-primary px-9 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:scale-[1.02]"
              style={{
                boxShadow: "0 4px 24px var(--color-brand-shadow)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 36px var(--color-brand-shadow-hover)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 4px 24px var(--color-brand-shadow)";
              }}
            >
              {t("forCompaniesPage.hero.ctaPrimary")}
              <ArrowRight
                className="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
                weight="bold"
              />
            </Link>
            <a
              href="#benefits"
              className="group inline-flex items-center gap-2 px-2 py-3 text-sm font-medium text-foreground"
            >
              <span
                className="border-b border-transparent pb-0.5 transition-colors group-hover:border-current"
              >
                {t("forCompaniesPage.hero.ctaSecondary")}
              </span>
              <ArrowDown
                className="size-3.5 transition-transform duration-300 group-hover:translate-y-0.5"
                weight="bold"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
